import type { ReactNode } from "react";
import { Avatar } from "../../components/index.ts";

/** "No coach yet": the blocks with no person. */
export const NO_ONE = "none";

/**
 * Who is on the week now: one face per person with a block on the week, with how many sessions
 * they take. A pointer on a face shows only that person's blocks on the week; a click keeps it
 * until a second click or Esc. Blocks with no coach have their own chip. Both views of the Schedule
 * page show it: the weekly times of the timetable, the sessions of one week.
 */
export function CoachStrip({
  items,
  team,
  staffPlural,
  highlight,
  pinned,
  onHover,
  onPin,
}: {
  /** Each block: who runs it, and how many sessions it is (a weekly time: one per day). */
  items: ReadonlyArray<{ staffUserId: string | null; sessions: number }>;
  team: Array<{ userId: string; name: string; photoUrl?: string | null }>;
  /** "coaches" */
  staffPlural: string;
  highlight: string | null;
  pinned: string | null;
  onHover: (userId: string | null) => void;
  onPin: (userId: string | null) => void;
}) {
  const weekly = new Map<string, number>();
  for (const item of items) {
    const who = item.staffUserId ?? NO_ONE;
    weekly.set(who, (weekly.get(who) ?? 0) + item.sessions);
  }
  const people = team.filter((member) => weekly.has(member.userId));
  const nobody = weekly.get(NO_ONE) ?? 0;
  if (people.length === 0 && nobody === 0) return null;

  const chip = (key: string, label: string, count: number, face: ReactNode) => {
    const on = pinned === key;
    return (
      <button
        key={key}
        type="button"
        aria-pressed={on}
        title={on ? `Show all ${staffPlural}` : `Show only ${label}`}
        onPointerEnter={() => onHover(key)}
        onPointerLeave={() => onHover(null)}
        onFocus={() => onHover(key)}
        onBlur={() => onHover(null)}
        onClick={() => onPin(on ? null : key)}
        className={[
          "flex shrink-0 items-center gap-1.5 rounded-full border-[1.5px] py-0.5 pr-2.5 pl-0.5 text-[12px] font-semibold transition-opacity",
          on ? "border-ink bg-sunken text-ink" : "border-line bg-paper text-ink-2 hover:border-ink-3",
          highlight && highlight !== key ? "opacity-50" : "",
        ].join(" ")}
      >
        {face}
        <span className="whitespace-nowrap">{label}</span>
        <span className="text-ink-3 tabular-nums">{count}</span>
      </button>
    );
  };

  return (
    <div className="flex items-center gap-2 overflow-x-auto border-b border-line bg-paper px-3 py-1.5">
      <span className="shrink-0 text-[11.5px] font-bold text-ink-2 capitalize">{staffPlural}</span>
      {people.map((member) => chip(member.userId, member.name, weekly.get(member.userId) ?? 0, <Avatar name={member.name} photoUrl={member.photoUrl ?? null} size={24} />))}
      {nobody
        ? chip(
            NO_ONE,
            "No one yet",
            nobody,
            <span aria-hidden="true" className="grid size-6 place-items-center rounded-full border border-dashed border-line-strong text-[11px] text-ink-3">
              ?
            </span>,
          )
        : null}
      <span className="ml-auto shrink-0 pl-2 text-[11px] text-ink-3">{pinned ? "Click again or press Esc to show all" : "Point at a face to see only the times of that person"}</span>
    </div>
  );
}
